import React, { useState, useRef, useEffect } from "react"
import { useNavigate, useLocation, useParams, Link } from "react-router-dom"
import { Menu, X, ChevronDown, MapPin, Route, Clock, Receipt, Bell, DollarSign, LayoutDashboard, FileText, HardHat, Wrench, Truck, Briefcase, SprayCan, ShieldCheck, Home, Package, HeartPulse, Zap } from "lucide-react"
import { useTranslation } from "react-i18next"
import LanguageSwitcher from "./LanguageSwitcher"
import { supportedLngs } from "../i18n"

const featureNav = [
  { slug: "live-gps-tracking", icon: MapPin, key: "liveGps" },
  { slug: "route-history", icon: Route, key: "routeHistory" },
  { slug: "attendance-tracking", icon: Clock, key: "attendance" },
  { slug: "expense-management", icon: Receipt, key: "expense" },
  { slug: "job-site-alerts", icon: Bell, key: "jobSiteAlerts" },
  { slug: "collection-management", icon: DollarSign, key: "collection" },
  { slug: "admin-dashboard", icon: LayoutDashboard, key: "adminDashboard" },
  { slug: "payroll-integration", icon: FileText, key: "payroll" },
]

const industryNav = [
  { slug: "construction", icon: HardHat, key: "construction" },
  { slug: "hvac-plumbing", icon: Wrench, key: "hvacPlumbing" },
  { slug: "delivery-logistics", icon: Truck, key: "deliveryLogistics" },
  { slug: "field-sales", icon: Briefcase, key: "fieldSales" },
  { slug: "cleaning-maintenance", icon: SprayCan, key: "cleaningMaintenance" },
  { slug: "security-services", icon: ShieldCheck, key: "securityServices" },
  { slug: "real-estate", icon: Home, key: "realEstate" },
  { slug: "fmcg-distribution", icon: Package, key: "fmcgDistribution" },
  { slug: "healthcare-homecare", icon: HeartPulse, key: "healthcareHomecare" },
  { slug: "electrical-services", icon: Zap, key: "electricalServices" },
]

function DropdownMenu({ isOpen, onClose, children }) {
  const ref = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose()
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div ref={ref} className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-4 px-6 min-w-[320px] z-50">
      {children}
    </div>
  )
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [mobileExpanded, setMobileExpanded] = useState(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { lang } = useParams()
  const { t } = useTranslation()
  const { t: tFeatures } = useTranslation("features")
  const { t: tIndustries } = useTranslation("industries")

  const currentLang = lang && supportedLngs.includes(lang) ? lang : "en"
  const prefix = `/${currentLang}`

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name)
  }

  const closeAll = () => {
    setOpenDropdown(null)
    setIsMenuOpen(false)
    setMobileExpanded(null)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="w-full px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20 md:h-24">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center gap-2 cursor-pointer" onClick={() => { navigate(`${prefix}/`); closeAll(); }}>
            <img
              src="/TrackTeam/trackteamlogo.png"
              alt="TrackTeam Logo"
              className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto hover:scale-105 transition-all duration-300"
            />
            <span className="text-base sm:text-lg md:text-xl font-bold text-[#62329f]">TrackTeam</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {/* Features Dropdown */}
            <div className="relative">
              <button
                className={`flex items-center gap-1 px-3 py-2 text-sm font-bold text-black hover:text-[#62329f] transition-colors ${openDropdown === "features" ? "text-[#62329f]" : ""}`}
                onClick={() => toggleDropdown("features")}
              >
                {t("nav.features")} <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === "features" ? "rotate-180" : ""}`} />
              </button>
              <DropdownMenu isOpen={openDropdown === "features"} onClose={() => setOpenDropdown(null)}>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 min-w-[420px]">
                  {featureNav.map((f) => {
                    const Icon = f.icon
                    return (
                      <Link
                        key={f.slug}
                        to={`${prefix}/features/${f.slug}`}
                        className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                        onClick={closeAll}
                      >
                        <Icon className="w-5 h-5 text-[#62329f] flex-shrink-0 mt-0.5" />
                        <span className="text-sm font-medium text-gray-700 text-left">{tFeatures(`${f.key}.title`)}</span>
                      </Link>
                    )
                  })}
                </div>
                <div className="border-t border-gray-100 mt-3 pt-3">
                  <Link to={`${prefix}/features`} className="text-sm font-semibold text-[#62329f] hover:underline" onClick={closeAll}>
                    {t("nav.allFeatures")} &rarr;
                  </Link>
                </div>
              </DropdownMenu>
            </div>

            {/* Industries Dropdown */}
            <div className="relative">
              <button
                className={`flex items-center gap-1 px-3 py-2 text-sm font-bold text-black hover:text-[#62329f] transition-colors ${openDropdown === "industries" ? "text-[#62329f]" : ""}`}
                onClick={() => toggleDropdown("industries")}
              >
                {t("nav.industries")} <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === "industries" ? "rotate-180" : ""}`} />
              </button>
              <DropdownMenu isOpen={openDropdown === "industries"} onClose={() => setOpenDropdown(null)}>
                <div className="space-y-0.5 min-w-[400px] max-h-[400px] overflow-y-auto">
                  {industryNav.map((ind) => {
                    const Icon = ind.icon
                    return (
                      <Link
                        key={ind.slug}
                        to={`${prefix}/industries/${ind.slug}`}
                        className="flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                        onClick={closeAll}
                      >
                        <Icon className="w-5 h-5 text-[#62329f] flex-shrink-0 mt-0.5" />
                        <span className="text-sm font-medium text-gray-700 text-left leading-snug">{tIndustries(`${ind.key}.title`)}</span>
                      </Link>
                    )
                  })}
                </div>
              </DropdownMenu>
            </div>

            {/* Pricing */}
            <Link
              to={`${prefix}/pricing`}
              className={`px-3 py-2 text-sm font-bold text-black hover:text-[#62329f] transition-colors ${location.pathname.includes("/pricing") ? "text-[#62329f]" : ""}`}
              onClick={closeAll}
            >
              {t("nav.pricing")}
            </Link>

            {/* Resources Dropdown */}
            <div className="relative">
              <button
                className={`flex items-center gap-1 px-3 py-2 text-sm font-bold text-black hover:text-[#62329f] transition-colors ${openDropdown === "resources" ? "text-[#62329f]" : ""}`}
                onClick={() => toggleDropdown("resources")}
              >
                {t("nav.resources")} <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === "resources" ? "rotate-180" : ""}`} />
              </button>
              <DropdownMenu isOpen={openDropdown === "resources"} onClose={() => setOpenDropdown(null)}>
                <div className="space-y-1">
                  <Link to={`${prefix}/about`} className="block px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700" onClick={closeAll}>
                    {t("nav.about")}
                  </Link>
                  <Link to={`${prefix}/contact`} className="block px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700" onClick={closeAll}>
                    {t("nav.contact")}
                  </Link>
                </div>
                <div className="border-t border-gray-100 mt-3 pt-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{t("nav.compare")}</p>
                  <div className="space-y-1">
                    {["hubstaff", "connecteam", "jibble", "timeero", "clockshark"].map((c) => (
                      <Link
                        key={c}
                        to={`${prefix}/compare/trackteam-vs-${c}`}
                        className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-600"
                        onClick={closeAll}
                      >
                        TrackTeam vs {c.charAt(0).toUpperCase() + c.slice(1)}
                      </Link>
                    ))}
                  </div>
                </div>
              </DropdownMenu>
            </div>
          </nav>

          {/* Right side */}
          <div className="flex items-center space-x-3">
            <div className="hidden lg:block">
              <LanguageSwitcher />
            </div>
            <a
              href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share"
              target="_blank"
              rel="noreferrer"
              className="hidden lg:inline-flex items-center justify-center rounded-full bg-[#62329f] text-white font-semibold px-6 py-3 border-2 border-black shadow-[4px_4px_0_#000] hover:shadow-[2px_2px_0_#000] transition-all duration-200"
            >
              {t('cta.getStarted')}
            </a>
            <button
              className="lg:hidden p-2 rounded-md text-black hover:bg-gray-100 transition-colors duration-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {/* Home */}
            <Link to={`${prefix}/`} className="block px-4 py-3 text-sm font-bold text-black hover:text-[#62329f] hover:bg-gray-50 rounded-lg" onClick={closeAll}>
              {t('nav.home')}
            </Link>

            {/* Features Accordion */}
            <div>
              <button
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-bold text-black hover:text-[#62329f] hover:bg-gray-50 rounded-lg"
                onClick={() => setMobileExpanded(mobileExpanded === "features" ? null : "features")}
              >
                {t("nav.features")}
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === "features" ? "rotate-180" : ""}`} />
              </button>
              {mobileExpanded === "features" && (
                <div className="pl-6 space-y-1">
                  {featureNav.map((f) => (
                    <Link key={f.slug} to={`${prefix}/features/${f.slug}`} className="block px-4 py-2 text-sm text-gray-600 hover:text-[#62329f] rounded-lg" onClick={closeAll}>
                      {tFeatures(`${f.key}.title`)}
                    </Link>
                  ))}
                  <Link to={`${prefix}/features`} className="block px-4 py-2 text-sm font-semibold text-[#62329f]" onClick={closeAll}>
                    {t("nav.allFeatures")} &rarr;
                  </Link>
                </div>
              )}
            </div>

            {/* Industries Accordion */}
            <div>
              <button
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-bold text-black hover:text-[#62329f] hover:bg-gray-50 rounded-lg"
                onClick={() => setMobileExpanded(mobileExpanded === "industries" ? null : "industries")}
              >
                {t("nav.industries")}
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === "industries" ? "rotate-180" : ""}`} />
              </button>
              {mobileExpanded === "industries" && (
                <div className="pl-6 space-y-1">
                  {industryNav.map((ind) => (
                    <Link key={ind.slug} to={`${prefix}/industries/${ind.slug}`} className="block px-4 py-2 text-sm text-gray-600 hover:text-[#62329f] rounded-lg" onClick={closeAll}>
                      {tIndustries(`${ind.key}.title`)}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Pricing */}
            <Link to={`${prefix}/pricing`} className="block px-4 py-3 text-sm font-bold text-black hover:text-[#62329f] hover:bg-gray-50 rounded-lg" onClick={closeAll}>
              {t("nav.pricing")}
            </Link>

            {/* About & Contact */}
            <Link to={`${prefix}/about`} className="block px-4 py-3 text-sm font-bold text-black hover:text-[#62329f] hover:bg-gray-50 rounded-lg" onClick={closeAll}>
              {t("nav.about")}
            </Link>
            <Link to={`${prefix}/contact`} className="block px-4 py-3 text-sm font-bold text-black hover:text-[#62329f] hover:bg-gray-50 rounded-lg" onClick={closeAll}>
              {t("nav.contact")}
            </Link>

            {/* Mobile Language Switcher */}
            <div className="pt-3 border-t border-gray-200">
              <LanguageSwitcher mobile />
            </div>

            {/* Mobile CTA */}
            <div className="pt-3 border-t border-gray-200">
              <a
                href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share"
                target="_blank"
                rel="noreferrer"
                className="block w-full text-center rounded-full bg-[#62329f] text-white font-semibold px-6 py-3 border-2 border-black shadow-[4px_4px_0_#000]"
                onClick={closeAll}
              >
                {t('cta.getStarted')}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
